import { useState, useMemo, useEffect } from 'react';
import debounce from 'lodash/debounce';

/**
 * Scaffolding for a server-backed multi-select typeahead with a label cache, shared by the cohort
 * builder's brand and shopper filters (and any future one). The caller owns the selected ids
 * (numbers) as the source of truth; this hook keeps an id→label cache so chips render names —
 * including ids hydrated from a duplicated broadcast.
 *
 * @param {Object}   opts
 * @param {number[]} opts.ids       Selected ids (source of truth, held by the caller).
 * @param {Function} opts.setIds    Setter for the selected ids.
 * @param {Function} opts.fetchFn   ({ search } | { ids }) => Promise<row[]>, each row { id, ... }.
 * @param {Function} opts.labelFn   (row) => string, builds the chip/option label.
 * @param {string}   opts.name      Label used in the hydration-failure console warning.
 * @returns {{ loadOptions, handleChange, value }} props for <AsyncMultiSelectFilter>.
 */
export const useAsyncLabeledSelect = ({ ids, setIds, fetchFn, labelFn, name }) => {
  const [labels, setLabels] = useState({});

  const mergeRows = rows => setLabels(prev => ({ ...prev, ...Object.fromEntries(rows.map(r => [r.id, labelFn(r)])) }));

  // Debounced so rapid typing collapses into one request; AsyncSelect's callback form resolves the
  // options once the debounce fires. fetchFn/labelFn are stable per filter, so a one-time memo is fine.
  const fetchOptions = useMemo(
    () =>
      debounce((input, callback) => {
        fetchFn({ search: input })
          .then(rows => {
            mergeRows(rows);
            callback(rows.map(r => ({ value: r.id, label: labelFn(r) })));
          })
          .catch(() => callback([]));
      }, 250),
    []
  );

  useEffect(() => () => fetchOptions.cancel(), [fetchOptions]);

  // Hydrate labels for any selected ids we don't have yet (e.g. loaded from a duplicated broadcast).
  useEffect(() => {
    const missing = ids.filter(id => !(id in labels));
    if (missing.length)
      fetchFn({ ids: missing })
        .then(mergeRows)
        .catch(err => console.warn(`[noir] failed to hydrate ${name} labels`, err));
  }, [ids]);

  const loadOptions = (input, callback) => fetchOptions(input, callback);

  const handleChange = opts => {
    setLabels(prev => ({ ...prev, ...Object.fromEntries(opts.map(o => [o.value, o.label])) }));
    setIds(opts.map(o => o.value));
  };

  const value = ids.map(id => ({ value: id, label: labels[id] || `#${id}` }));

  return { loadOptions, handleChange, value };
};
